import { makeStyles } from '@material-ui/styles'
import { listTablePrefs } from 'app/constants'
import { IVmDetailsPageTabs } from 'app/plugins/openstack/components/vms/vm-details/model'
import InferActionParams from 'core/actions/InferActionParams'
import CreateButton from 'core/components/buttons/CreateButton'
import SimpleLink from 'core/components/SimpleLink'
import ListContainer from 'core/containers/ListContainer'
import { createGridStatusCell, StatusCellModel } from 'core/elements/grid/cells/GridStatusCell'
import getGridDialogButton from 'core/elements/grid/helpers/getGridDialogButton'
import { GridRowMenuItemSpec } from 'core/elements/grid/hooks/useGridRowMenu'
import { GridBatchActionSpec } from 'core/elements/grid/hooks/useGridSelectableRows'
import { SortingState } from 'core/elements/grid/hooks/useGridSorting'
import Text from 'core/elements/Text'
import Tooltip from 'core/elements/tooltip/Tooltip'
import useGlobalParams from 'core/hooks/useGlobalParams'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { isReadonlyUser } from 'core/utils/helpers'
import { routeManager } from 'core/utils/routes'
import { GridCellProps } from 'pf9-ui-components/built/elements/grid/hooks/useGridRows'
import ToggleSwitch from 'pf9-ui-components/built/elements/ToggleSwitch'
import { pick } from 'ramda'
import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { isNilOrEmpty } from 'utils/fp'
import { kaapiClustersSelector } from '../kaapi/clusters/selectors'
import { IMachineDeploymentSelector } from '../kaapi/machine-deployment/model'
import { listKaapiMachines } from '../kaapi/machines/actions'
import { kaapiMachinesForClusterSelector } from '../kaapi/machines/selectors'
import AddPhysicalNodes from './AddPhysicalNodes'
import DeleteWorkerNodesDialog from './DeleteWorkerNodesDialog'
import GPUDetailsModal from '../clusters-dashboard/GPUDetailsModal'
import { DateAndTime } from 'core/components/listTable/cells/DateCell'
import UsageProgress from '../capacity-and-health/UsageProgress'
import { GridViewColumn } from 'core/elements/grid/Grid'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import Theme from 'core/themes/model'

type ActionParams = InferActionParams<typeof listKaapiMachines>
type Params = ActionParams & {}

const defaultParams: Params & SortingState = {
  orderBy: 'metadata.creationTimestamp',
  orderDirection: 'desc',
}

const usePrefParams = createUsePrefParamsHook('KaapiMachines', listTablePrefs)

const useWorkerNodeCellComponentStyles = makeStyles((theme: Theme) => ({
  disabled: {
    cursor: 'not-allowed',
  },
  warning: {
    background: theme.palette.graphYellow[100],
    color: theme.palette.graphYellow[500],
  },
  nameContainer: {
    display: 'grid',
    gridTemplateColumns: 'auto auto',
    alignItems: 'center',
  },
}))

export type NodeGroupSelector = IMachineDeploymentSelector

const Phase = {
  Provisioning: 'Provisioning',
  Provisioned: 'Provisioned',
  Running: 'Running',
  Failed: 'Failed',
  ScalingUp: 'ScalingUp',
  ScalingDown: 'ScalingDown',
  Deleting: 'Deleting',
  Unknown: 'Unknown',
  Ready: 'Ready',
  Pending: 'Pending',
}

const MACHINE_FAILURE_PHASES = [Phase.Failed, Phase.Deleting, Phase.Unknown]

// OpenStack Machine States
export const getMachineStatus = (phase): StatusCellModel => {
  switch (phase) {
    case Phase.Running:
    case Phase.Provisioned:
    case Phase.Ready:
      return { variant: 'success', label: phase }
    case Phase.Provisioning:
    case Phase.ScalingUp:
    case Phase.ScalingDown:
    case Phase.Pending:
      return { variant: 'warning', label: phase }
    case Phase.Failed:
    case Phase.Deleting:
      return { variant: 'error', label: phase }
    default:
      return { variant: 'unknown', label: phase || Phase.Unknown }
  }
}

// K8s Node States
export const getK8sNodeStatus = (phase): StatusCellModel => {
  switch (phase) {
    case 'Ready':
      return { variant: 'success', label: phase }
    case 'MemoryPressure':
    case 'DiskPressure':
    case 'PIDPressure':
      return { variant: 'warning', label: phase }
    case 'NetworkUnavailable':
    case 'NotReady':
      return { variant: 'error', label: phase }
    default:
      return { variant: 'unknown', label: phase || Phase.Unknown }
  }
}

const cellFormatFn = (value) => (isNilOrEmpty(value) ? '-' : value)

// Extract UUID from providerId Eg. openstack:///283d5beb-4f02-4fd5-ad4f-ebc7cf472060
function extractUUID(providerId) {
  const match = providerId?.match(/[0-9a-fA-F-]{36}/)
  return match ? match[0] : null
}

export function WorkerNodeCellComponent({ value, item }: GridCellProps<any>) {
  const classes = useWorkerNodeCellComponentStyles()

  const vmID = extractUUID(item?.spec?.providerID)
  const linkToVM = routeManager.routes.openstack.vmDetails.path({
    id: vmID,
    tab: IVmDetailsPageTabs.Overview,
  })
  const isMachineFailure = item?.isBYOMachine || MACHINE_FAILURE_PHASES.includes(item?.phase)

  return (
    <div className={classes.nameContainer}>
      {isMachineFailure ? (
        <Text variant="body2">{value as string}</Text>
      ) : (
        <Tooltip message={!vmID ? 'VM not provisioned yet' : ''}>
          <SimpleLink className={!vmID && classes.disabled} src={vmID ? linkToVM : null}>
            {value as string}
          </SimpleLink>
        </Tooltip>
      )}
      {item?.unschedulable && (
        <Tooltip message="Scheduling disabled">
          <FontAwesomeIcon className={classes.warning}>triangle-exclamation</FontAwesomeIcon>
        </Tooltip>
      )}
    </div>
  )
}

export const GPUMemoryCellComponent = ({ value }) => (
  <Text variant="body2">{`${(Number(value) / 1028).toFixed(2)} GB` || '-'}</Text>
)

export const workerNodeColumns = (isBYOHCluster: boolean): GridViewColumn<any>[] => [
  {
    key: 'name',
    label: 'Name',
    CellComponent: WorkerNodeCellComponent,
  },
  {
    key: 'gpu.enabled',
    label: 'GPU Enabled',
    CellComponent: ({ value }) => <Text variant="body2">{value ? 'Yes' : 'No'}</Text>,
  },
  {
    key: 'gpu.model',
    label: 'GPU Model',
    CellComponent: ({ value }) => <Text variant="body2">{value || '-'}</Text>,
    display: false,
  },
  {
    key: 'gpu.partitioningStrategy',
    label: 'GPU Strategy',
    CellComponent: ({ value, item }) => <GPUDetailsModal item={item} value={value} />,
    display: false,
  },
  {
    key: 'gpu.details',
    label: 'GPU Details',
    CellComponent: ({ value }) => <Text variant="body2">{value || '-'}</Text>,
    display: false,
  },
  {
    key: 'gpu.count',
    label: 'GPU Count',
    CellComponent: ({ value }) => <Text variant="body2">{value || '-'}</Text>,
    display: false,
  },
  {
    key: 'gpu.replicas',
    label: 'GPU Replicas',
    CellComponent: ({ value }) => <Text variant="body2">{value || '-'}</Text>,
    display: false,
  },
  {
    key: 'gpu.memory',
    label: 'GPU Memory',
    CellComponent: GPUMemoryCellComponent,
    display: false,
  },
  {
    key: 'k8sNodeStatusType',
    label: 'K8s Node status',
    CellComponent: createGridStatusCell({
      dataFn: (node: NodeGroupSelector): StatusCellModel => {
        return getK8sNodeStatus(node)
      },
    }),
  },
  {
    key: 'phase',
    label: 'Machine Status',
    CellComponent: createGridStatusCell({
      dataFn: (node: NodeGroupSelector): StatusCellModel => {
        return getMachineStatus(node)
      },
    }),
  },
  {
    key: 'machineDeployment.metadata.name',
    label: 'Node Group',
    display: false,
  },
  ...[
    isBYOHCluster
      ? {
          key: 'physicalNode',
          label: 'Physical Node',
        }
      : {
          key: 'machineDeployment.openStackMachineTemplate.spec.template.spec.flavor',
          label: 'Flavor',
        },
  ],
  {
    key: 'spec.version',
    label: 'K8s Version',
    formatFn: (value) => (isNilOrEmpty(value) ? '-' : value),
  },
  {
    key: 'metadata.creationTimestamp',
    label: 'Created',
    CellComponent: DateAndTime,
  },
  {
    key: 'cpuUsage',
    label: 'CPU Usage (cores)',
    CellComponent: UsageProgress,
  },
  {
    key: 'memoryUsage',
    label: 'Memory Usage (GiB)',
    CellComponent: UsageProgress,
  },
]

export default function ListWorkerNodes() {
  const { allParams: params, getParamsUpdater } = useGlobalParams(usePrefParams, defaultParams)
  const { prefs: k8sPluginGlobalPerfs } = useScopedPreferences('k8sPluginGlobalParams')

  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [selectedWorkerNode, setSelectedWorkerNode] = useState(null)
  const [showAddPhysicalNodesModal, setShowAddPhysicalNodesModal] = useState(false)
  const [showGpuNodesOnly, setShowGpuNodesOnly] = useState(false)

  // Refresh the node list when the cluster changes
  useEffect(() => {
    if (k8sPluginGlobalPerfs.cluster) {
      reloadKaapiMachines(true, false)
    }
  }, [k8sPluginGlobalPerfs.cluster])

  const allClusters = useSelector(kaapiClustersSelector)
  const cluster = allClusters?.find((c) => c?.metadata?.name === k8sPluginGlobalPerfs?.cluster)

  // Fetch Worker Nodes
  const { loading: loadingKaapiMachines, reload: reloadKaapiMachines } = useListAction(
    listKaapiMachines,
  )
  const machinesSelector = kaapiMachinesForClusterSelector({
    clusterName: k8sPluginGlobalPerfs?.cluster,
  })
  const machines = useSelector(machinesSelector) || []

  // Check if this is the last running node in the cluster
  const isLastRunningNode: boolean = useMemo(
    () => machines?.length === 1 && machines[0]?.status.phase === 'Running',
    [machines],
  )

  const batchActions: GridBatchActionSpec<any>[] = [
    {
      label: 'Delete',
      icon: 'trash-alt',
      handleAction: (workerNode) => {
        setSelectedWorkerNode(workerNode)
        setShowDeleteDialog(true)
      },
    },
  ]

  const rowMenuItems: GridRowMenuItemSpec<any>[] = [
    {
      label: 'Delete',
      icon: 'trash-alt',
      handleClick: (workerNode) => {
        setSelectedWorkerNode([workerNode])
        setShowDeleteDialog(true)
      },
      refreshAfterSuccess: true,
      hideIfDisabled: true,
    },
  ]

  const showBYOAddNodesButton = !isReadonlyUser() && cluster?.isBYOHCluster

  // Filter machines based on showGpuNodesOnly
  const filteredMachines = useMemo(() => {
    if (showGpuNodesOnly) {
      return machines.filter((machine) => !!machine?.gpu?.enabled)
    }
    return machines
  }, [machines, showGpuNodesOnly])

  return (
    <>
      {showDeleteDialog && (
        <DeleteWorkerNodesDialog
          data-testid="delete-worker-nodes-dialog"
          rows={selectedWorkerNode}
          onClose={() => setShowDeleteDialog(false)}
          isLastRunningNode={isLastRunningNode}
          reloadKaapiMachines={reloadKaapiMachines}
          isBYOHCluster={cluster?.isBYOHCluster}
        />
      )}
      {showAddPhysicalNodesModal && (
        <AddPhysicalNodes
          onClose={() => setShowAddPhysicalNodesModal(false)}
          clusterName={cluster?.metadata?.name}
        />
      )}

      <ListContainer<any, any>
        data-testid="worker-nodes-list-container"
        searchTargets={['name', 'gpuModel', 'phase']}
        uniqueIdentifier="uid"
        loading={loadingKaapiMachines}
        loadingMessage="Loading..."
        multiSelection
        data={filteredMachines}
        columns={workerNodeColumns(cluster?.isBYOHCluster)}
        getParamsUpdater={getParamsUpdater}
        label="Worker Nodes"
        onRefresh={reloadKaapiMachines}
        showItemsCountInLabel
        showBreadcrumbs={false}
        batchActions={batchActions}
        rowMenuItems={rowMenuItems}
        showRowMenuForSingleRowActions
        {...pick(listTablePrefs, params)}
        extraToolbarContent={
          <div style={{ display: 'flex', gap: '16px' }}>
            <ToggleSwitch
              active={showGpuNodesOnly}
              onClick={() => setShowGpuNodesOnly(!showGpuNodesOnly)}
              label="Show GPU Nodes Only"
              data-testid="show-gpu-nodes-only-toggle-switch"
            />
            {showBYOAddNodesButton && (
              <CreateButton
                icon="add"
                onClick={() => setShowAddPhysicalNodesModal(true)}
                data-testid="add-node-group-button"
              >
                Add Physical Nodes
              </CreateButton>
            )}
          </div>
        }
      />
    </>
  )
}
